import { Tag } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, withAuthContext } from '@amzn/awscat-react-components';
import { Button, Container, FormField, Header, TagEditor, TagEditorProps } from '@amzn/awsui-components-react';

import { constants } from '../../common/Localization';
import { format, generatePluralStr, isAWSUser } from '../../common/util';

interface Props {
    tags: Tag[],
    setTags: (tags: Tag[]) => void,
    loading?: boolean,
    updating?: boolean,
}

const suggestedTags = new Map<string, string[]>([
    [constants.TAG_EDITOR_SAP_ARR, []],
    [constants.TAG_EDITOR_SAP_REPORTING, [constants.YES, constants.NO]],
    [constants.TAG_EDITOR_SFDC_OPPORTUNITY_ID, []],
    [constants.TAG_EDITOR_SPECIALIZED_WORKLOAD_SAP, [constants.YES, constants.NO]],
    [constants.TAG_EDITOR_SPECIALIZED_WORKLOAD_ORACLE, [constants.YES, constants.NO]],
    [constants.TAG_EDITOR_SPECIALIZED_WORKLOAD_MICROSOFT, [constants.YES, constants.NO]],
]);

function convertToTagEditorPropsTags(tags: Tag[]): TagEditorProps.Tag[] {
    return tags.map(tag => {
        return {...tag, existing: false};
    });
}

function convertTagEditorPropsTags(array: readonly TagEditorProps.Tag[]): Tag[] {
    return array.map(tag => {
        return { key: tag.key, value: tag.value };
    });
}

const PortfolioTagEditor = (props: Props & AuthContextInterface): JSX.Element => {
    const { tags, setTags, loading, updating } = props;

    const keysRequest = async (): Promise<string[]> => Array.from(suggestedTags.keys());
    const valuesRequest = async (key: string): Promise<string[]> => key ? suggestedTags.get(key) ?? [] : [];

    const addSAPTags = (): void => {
        const tagKeys = tags.map(tag => tag.key);
        if (!tagKeys.includes(constants.TAG_EDITOR_SPECIALIZED_WORKLOAD_SAP)) {
            tags.push({ key: constants.TAG_EDITOR_SPECIALIZED_WORKLOAD_SAP, value: constants.YES,});
            setTags(tags);
        }
        if (!tagKeys.includes(constants.TAG_EDITOR_SFDC_OPPORTUNITY_ID)) {
            tags.push({ key: constants.TAG_EDITOR_SFDC_OPPORTUNITY_ID, value: '',});
            setTags(tags);
        }
        if (!tagKeys.includes(constants.TAG_EDITOR_SAP_REPORTING)) {
            tags.push({ key: constants.TAG_EDITOR_SAP_REPORTING, value: constants.YES,});
            setTags(tags);
        }
        if (!tagKeys.includes(constants.TAG_EDITOR_SAP_ARR)) {
            tags.push({ key: constants.TAG_EDITOR_SAP_ARR, value: '',});
            setTags(tags);
        }
    };

    const addSAPTagsButton =  loading || updating || !isAWSUser(props.auth) ? null : (
        <Button iconName="add-plus" onClick={addSAPTags}>{constants.TAG_EDITOR_ADD_SAP_TAGS}</Button>
    );

    return (
        <Container header={
            <Header variant="h2" actions={addSAPTagsButton}>
                {constants.EDIT_PORTFOLIO_TAGS_HEADER}
            </Header>
        }>
            <FormField>
                <TagEditor
                    i18nStrings={{
                        keyPlaceholder: constants.TAG_EDITOR_KEY_PLACEHOLDER,
                        valuePlaceholder: constants.TAG_EDITOR_VALUE_PLACEHOLDER,
                        addButton: constants.TAG_EDITOR_ADD_BUTTON,
                        removeButton: constants.TAG_EDITOR_REMOVE_BUTTON,
                        undoButton: constants.TAG_EDITOR_UNDO_BUTTON,
                        undoPrompt: constants.TAG_EDITOR_UNDO_PROMPT,
                        loading: updating ? constants.TAG_EDITOR_UPDATING : constants.TAG_EDITOR_LOADING,
                        keyHeader: constants.TAG_EDITOR_KEY_HEADER,
                        valueHeader: constants.TAG_EDITOR_VALUE_HEADER,
                        optional: constants.TAG_EDITOR_OPTIONAL,
                        keySuggestion: constants.TAG_EDITOR_KEY_SUGGESTION,
                        valueSuggestion: constants.TAG_EDITOR_VALUE_SUGGESTION,
                        emptyTags: constants.TAG_EDITOR_EMPTY_TAGS,
                        tooManyKeysSuggestion: constants.TAG_EDITOR_TOO_MANY_KEYS_SUGGESTION,
                        tooManyValuesSuggestion: constants.TAG_EDITOR_TOO_MANY_VALUES_SUGGESTION,
                        keysSuggestionLoading: constants.TAG_EDITOR_KEYS_SUGGESTION_LOADING,
                        keysSuggestionError: constants.TAG_EDITOR_KEYS_SUGGESTION_ERROR,
                        valuesSuggestionLoading: constants.TAG_EDITOR_VALUES_SUGGESTION_LOADING,
                        valuesSuggestionError: constants.TAG_EDITOR_VALUES_SUGGESTION_ERROR,
                        emptyKeyError: constants.TAG_EDITOR_EMPTY_KEY_ERROR,
                        maxKeyCharLengthError: constants.TAG_EDITOR_MAX_KEY_CHAR_LENGTH_ERROR,
                        maxValueCharLengthError: constants.TAG_EDITOR_MAX_VALUE_CHAR_LENGTH_ERROR,
                        duplicateKeyError: constants.TAG_EDITOR_DUPLICATE_KEY_ERROR,
                        invalidKeyError: constants.TAG_EDITOR_INVALID_KEY_ERROR,
                        invalidValueError: constants.TAG_EDITOR_INVALID_VALUE_ERROR,
                        awsPrefixError: constants.TAG_EDITOR_AWS_PREFIX_ERROR,
                        tagLimit: availableTags => format(generatePluralStr(
                            availableTags, constants.TAG_EDITOR_TAG_LIMIT, undefined, false), [availableTags.toString()]),
                        tagLimitReached: tagLimit => format(generatePluralStr(
                            tagLimit, constants.TAG_EDITOR_TAG_LIMIT_REACHED, undefined, false), [tagLimit.toString()]),
                        tagLimitExceeded: tagLimit => format(generatePluralStr(
                            tagLimit, constants.TAG_EDITOR_TAG_LIMIT_EXCEEDED, undefined, false), [tagLimit.toString()]),
                        enteredKeyLabel: key => format(constants.TAG_EDITOR_ENTERED_KEY_VALUE_LABEL, [key]),
                        enteredValueLabel: value => format(constants.TAG_EDITOR_ENTERED_KEY_VALUE_LABEL, [value]),
                    }}
                    loading={loading || updating}
                    tags={convertToTagEditorPropsTags(tags)}
                    onChange={({ detail }) => {
                        const newTags = convertTagEditorPropsTags(detail.tags);
                        setTags(newTags);
                    }}
                    keysRequest={keysRequest}
                    valuesRequest={valuesRequest}
                />
            </FormField>
        </Container>
    );
};

export default withAuthContext(PortfolioTagEditor);
